import {
  RfqEmailQuoteAnalysisResponse,
  RfqExtractedQuoteReadingResponse,
  RfqExtractedQuoteResponse,
  RfqItemResponse,
} from '@interfaces/risk-monitoring/responses/rfq.response';

/**
 * Leitura gerencial de uma cotação extraída pela IA (card 3545): troca o texto técnico do agente
 * por frases que dizem o que o banco cotou, o que isso significa para a empresa e o que conferir.
 *
 * Este arquivo só MONTA AS FRASES. Todo número derivado (forward vs. spot, valores em R$ no notional,
 * folga até o CAP, proteção do spread, prêmio total, câmbio efetivo, "bate com o pedido") vem pronto
 * do backend em `quote.reading` (`RfqQuoteReadingCalculator`), e a confiança da IA e o aviso de spot
 * do sistema vêm em `analysis.matchConfidence` / `analysis.spotFilledBySystem`: cálculo financeiro
 * não nasce no front.
 */
export interface RfqQuoteInsightLine {
  text: string;
  tone: 'info' | 'ok' | 'warn';
}

export interface RfqQuoteInsight {
  title: string;
  lines: RfqQuoteInsightLine[];
  hasWarning: boolean;
  responder: string | null;
}

/** Abaixo disso, a IA não tem certeza de que a cotação é deste item: a linha ganha o aviso ⚠. */
const LOW_MATCH_CONFIDENCE = 0.8;

export function buildRfqQuoteInsight(
  analysis: RfqEmailQuoteAnalysisResponse,
  item: RfqItemResponse | null,
  quote: RfqExtractedQuoteResponse | null,
  bankName: string | null | undefined,
  kindLabel: string,
  /** Data no formato do usuário: quem chama passa o `DateFormatterService`. */
  formatDate: (date: string) => string
): RfqQuoteInsight {
  const currency = item?.currencyCode || '';
  const bank = bankName || 'O banco';
  const titleParts = [
    `${bank} cotou ${kindLabel}`,
    item?.maturityDate ? `para ${formatDate(item.maturityDate)}` : null,
    item?.notional ? `· ${currency} ${formatNumber(item.notional, 2)}` : null,
  ];

  const lines: RfqQuoteInsightLine[] = [];
  if (!quote) {
    lines.push({
      text: analysis.parseError
        ? 'A IA leu a resposta, mas não conseguiu extrair os valores deste item. Confira no e-mail abaixo.'
        : 'O banco não cotou este item nesta resposta.',
      tone: 'warn',
    });
  } else {
    lines.push(...buildKindLines(item, quote, currencyUnitLabel(currency), quote.reading ?? {}));
    lines.push(...buildSpotLines(analysis, quote));
    lines.push(...buildMatchLines(analysis));
  }

  return {
    title: titleParts.filter(Boolean).join(' '),
    lines,
    hasWarning: lines.some(l => l.tone === 'warn'),
    responder: buildResponder(analysis),
  };
}

function buildKindLines(
  item: RfqItemResponse | null,
  quote: RfqExtractedQuoteResponse,
  unit: string,
  reading: RfqExtractedQuoteReadingResponse
): RfqQuoteInsightLine[] {
  switch (quote.kind) {
    case 'NdfVanilla':
      return quote.forward
        ? [forwardLine(quote.forward, quote.spotRate, unit, reading)]
        : [missing('o forward')];

    case 'NdfCap': {
      const lines = [
        quote.forward
          ? forwardLine(quote.forward, quote.spotRate, unit, reading)
          : missing('o forward'),
      ];
      if (!quote.cap) return [...lines, missing('o CAP')];
      lines.push(
        requestLine(
          `Teto (CAP): R$ ${formatNumber(quote.cap, 4)}`,
          reading.matchesRequest,
          item?.kind === 'NdfCap' ? `CAP pedido: ${formatNumber(item.cap, 4)}` : null,
          `Até esse câmbio a parcela fica protegida; acima dele, não há proteção contra a alta do ${unit}.`
        )
      );
      if (hasValue(reading.capHeadroomRatio)) {
        lines.push({
          text: `Folga até o teto: ${formatPercent(reading.capHeadroomRatio!)} acima do câmbio de hoje (spot).`,
          tone: 'info',
        });
      }
      return lines;
    }

    case 'CallVanilla':
    case 'PutVanilla': {
      if (!quote.strike) return [missing('o strike')];
      const isCall = quote.kind === 'CallVanilla';
      const lines = [
        requestLine(
          `Direito de ${isCall ? 'comprar' : 'vender'} o ${unit} a R$ ${formatNumber(quote.strike, 4)} (strike)`,
          reading.matchesRequest,
          item?.kind === 'CallVanilla' || item?.kind === 'PutVanilla'
            ? `strike pedido: ${formatNumber(item.strike, 4)}`
            : null,
          isCall
            ? `Se o ${unit} passar desse valor no vencimento, a opção paga a diferença.`
            : `Se o ${unit} cair abaixo desse valor no vencimento, a opção paga a diferença.`
        ),
      ];
      if (quote.premium) lines.push(premiumLine(quote.premium, unit, reading));
      if (hasValue(reading.effectiveRate)) {
        lines.push({
          text: isCall
            ? `Câmbio máximo para a empresa: R$ ${formatNumber(reading.effectiveRate!, 4)} (strike + prêmio).`
            : `Câmbio mínimo para a empresa: R$ ${formatNumber(reading.effectiveRate!, 4)} (strike − prêmio).`,
          tone: 'info',
        });
      }
      return lines;
    }

    case 'CallSpread': {
      const { lowStrike: low, highStrike: high } = quote;
      if (!low || !high) return [missing('os strikes')];
      const lines: RfqQuoteInsightLine[] = [
        {
          text: `Faixa de proteção: de R$ ${formatNumber(low, 4)} a R$ ${formatNumber(high, 4)} por ${unit}. Se o ${unit} passar de ${formatNumber(low, 4)} no vencimento, a estrutura paga a diferença, até o limite de ${formatNumber(high, 4)}.`,
          tone: 'info',
        },
        ...protectionLines(unit, reading),
      ];
      if (quote.premium) lines.push(premiumLine(quote.premium, unit, reading));
      if (hasValue(reading.effectiveRate) && hasValue(reading.maxProtectionPerUnit)) {
        lines.push({
          text: `Câmbio efetivo: abaixo de ${formatNumber(low, 4)}, o do dia + prêmio; entre ${formatNumber(low, 4)} e ${formatNumber(high, 4)}, fica em ${formatNumber(reading.effectiveRate!, 4)}; acima de ${formatNumber(high, 4)}, volta a subir (${unit} − ${formatNumber(reading.maxProtectionPerUnit!, 4)} + prêmio).`,
          tone: 'info',
        });
      }
      lines.push(...strikesLines(item, reading));
      return lines;
    }

    case 'PutSpread': {
      const { lowStrike: low, highStrike: high } = quote;
      if (!low || !high) return [missing('os strikes')];
      const lines: RfqQuoteInsightLine[] = [
        {
          text: `Faixa de proteção na queda: de R$ ${formatNumber(high, 4)} até R$ ${formatNumber(low, 4)} por ${unit}. Se o ${unit} cair abaixo de ${formatNumber(high, 4)} no vencimento, a estrutura paga a diferença, até o limite de ${formatNumber(low, 4)}.`,
          tone: 'info',
        },
        ...protectionLines(unit, reading),
      ];
      if (quote.premium) lines.push(premiumLine(quote.premium, unit, reading));
      lines.push(...strikesLines(item, reading));
      return lines;
    }

    case 'Collar': {
      // Collar no Cognito: lowStrike = put vendida, highStrike = call comprada (proteção de quem
      // compra a moeda, como o importador e a dívida em moeda estrangeira).
      const { lowStrike: putStrike, highStrike: callStrike } = quote;
      if (!putStrike || !callStrike) return [missing('os strikes')];
      const lines: RfqQuoteInsightLine[] = [
        {
          text: `Teto: R$ ${formatNumber(callStrike, 4)} por ${unit}. Acima disso, a estrutura paga a diferença para a empresa.`,
          tone: 'info',
        },
        {
          text: `Piso: R$ ${formatNumber(putStrike, 4)} por ${unit}. Abaixo disso, a empresa paga a diferença ao banco (abre mão da queda do câmbio).`,
          tone: 'info',
        },
        quote.premium
          ? premiumLine(quote.premium, unit, reading)
          : { text: 'Sem prêmio: o piso financia o teto.', tone: 'info' },
        ...strikesLines(item, reading),
      ];
      return lines;
    }

    case 'Loan4131': {
      const lines: RfqQuoteInsightLine[] = [];
      if (hasValue(quote.allInRatePercentage)) {
        lines.push({
          text: `Custo total (all-in): ${formatNumber(quote.allInRatePercentage!, 4)}% a.a.`,
          tone: 'info',
        });
      }
      if (hasValue(quote.spreadPercentage)) {
        lines.push({
          text: `Spread: ${formatNumber(quote.spreadPercentage!, 4)}% a.a.${quote.indexer ? ` sobre ${quote.indexer}` : ''}.`,
          tone: 'info',
        });
      }
      if (quote.conditions) lines.push({ text: `Condições: ${quote.conditions}`, tone: 'info' });
      return lines.length ? lines : [missing('a taxa')];
    }

    default:
      return [];
  }
}

function forwardLine(
  forward: number,
  spot: number | null | undefined,
  unit: string,
  reading: RfqExtractedQuoteReadingResponse
): RfqQuoteInsightLine {
  let vsSpot = '';
  if (hasValue(reading.forwardVsSpotRatio) && spot) {
    const ratio = reading.forwardVsSpotRatio!;
    vsSpot =
      Math.abs(ratio) < 0.0005
        ? ', praticamente igual ao câmbio de hoje'
        : `, ${formatPercent(Math.abs(ratio))} ${ratio > 0 ? 'acima' : 'abaixo'} do câmbio de hoje (${formatNumber(spot, 4)})`;
  }
  const total = hasValue(reading.notionalValueBrl)
    ? ` No notional, ≈ ${formatCompactBrl(reading.notionalValueBrl!)}.`
    : '';
  return {
    text: `Câmbio travado (forward): R$ ${formatNumber(forward, 4)} por ${unit}${vsSpot}.${total}`,
    tone: 'info',
  };
}

function protectionLines(
  unit: string,
  reading: RfqExtractedQuoteReadingResponse
): RfqQuoteInsightLine[] {
  if (!hasValue(reading.maxProtectionPerUnit)) return [];
  const total = hasValue(reading.maxProtectionBrl)
    ? ` (≈ ${formatCompactBrl(reading.maxProtectionBrl!)} no notional)`
    : '';
  return [
    {
      text: `Proteção máxima: R$ ${formatNumber(reading.maxProtectionPerUnit!, 4)} por ${unit}${total}.`,
      tone: 'info',
    },
  ];
}

function premiumLine(
  premium: number,
  unit: string,
  reading: RfqExtractedQuoteReadingResponse
): RfqQuoteInsightLine {
  const total = hasValue(reading.premiumTotalBrl)
    ? `, ≈ ${formatCompactBrl(reading.premiumTotalBrl!)} no total`
    : '';
  return {
    text: `Custo (prêmio): R$ ${formatNumber(premium, 4)} por ${unit}${total}, pago na contratação.`,
    tone: 'info',
  };
}

/** Linha de um valor cotado com a comparação ao pedido, que vem pronta do backend. */
function requestLine(
  label: string,
  matchesRequest: boolean | null | undefined,
  requestedLabel: string | null,
  meaning: string
): RfqQuoteInsightLine {
  if (matchesRequest === true)
    return { text: `${label}, igual ao pedido. ${meaning}`, tone: 'info' };
  if (matchesRequest === false) {
    return {
      text: `${label}, diferente do pedido${requestedLabel ? ` (${requestedLabel})` : ''}. ${meaning}`,
      tone: 'warn',
    };
  }
  return { text: `${label}. ${meaning}`, tone: 'info' };
}

function strikesLines(
  item: RfqItemResponse | null,
  reading: RfqExtractedQuoteReadingResponse
): RfqQuoteInsightLine[] {
  if (reading.matchesRequest === true)
    return [{ text: 'Strikes iguais aos pedidos.', tone: 'info' }];
  if (reading.matchesRequest !== false) return [];
  const requested =
    item && 'lowStrike' in item && 'highStrike' in item
      ? ` (pedido: ${formatNumber(item.lowStrike, 4)} / ${formatNumber(item.highStrike, 4)})`
      : '';
  return [{ text: `Strikes diferentes do pedido${requested}.`, tone: 'warn' }];
}

function buildSpotLines(
  analysis: RfqEmailQuoteAnalysisResponse,
  quote: RfqExtractedQuoteResponse
): RfqQuoteInsightLine[] {
  if (quote.kind === 'Loan4131' || !analysis.spotFilledBySystem || !quote.spotRate) return [];
  return [
    {
      text: `Câmbio de hoje (R$ ${formatNumber(quote.spotRate, 4)}) é a última cotação do sistema: o banco não informou o spot no e-mail.`,
      tone: 'info',
    },
  ];
}

function buildMatchLines(analysis: RfqEmailQuoteAnalysisResponse): RfqQuoteInsightLine[] {
  const confidence = analysis.matchConfidence;
  if (!hasValue(confidence)) return [];
  if (confidence! < LOW_MATCH_CONFIDENCE) {
    return [
      {
        text: 'Confira: a IA não teve certeza de que esta cotação é deste item. Compare com a tabela do e-mail.',
        tone: 'warn',
      },
    ];
  }
  return [{ text: 'A IA ligou esta cotação ao item pedido com confiança alta.', tone: 'ok' }];
}

function missing(what: string): RfqQuoteInsightLine {
  return {
    text: `O banco não informou ${what} deste item. Confira no e-mail abaixo.`,
    tone: 'warn',
  };
}

function buildResponder(analysis: RfqEmailQuoteAnalysisResponse): string | null {
  const r = analysis.responder;
  const parts = [r?.name, r?.position, r?.phone].filter(Boolean);
  return parts.length ? `Cotado por ${parts.join(' · ')}` : null;
}

function hasValue(value: number | null | undefined): boolean {
  return value !== null && value !== undefined;
}

function currencyUnitLabel(currency: string): string {
  switch (currency.toUpperCase()) {
    case 'USD':
      return 'dólar';
    case 'EUR':
      return 'euro';
    case 'GBP':
      return 'libra';
    case 'JPY':
      return 'iene';
    case 'CHF':
      return 'franco';
    case 'CNY':
      return 'yuan';
    default:
      return currency || 'unidade';
  }
}

function formatNumber(value: number, digits: number): string {
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/** Razão (0,0059) exibida como percentual (0,6%). Só formatação: o valor vem do backend. */
function formatPercent(ratio: number): string {
  return `${(ratio * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** Valor em R$ abreviado (mil / mi) para caber na frase. Só formatação: o valor vem do backend. */
function formatCompactBrl(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `R$ ${formatNumber(value / 1_000_000, 1)} mi`;
  if (abs >= 1_000) return `R$ ${formatNumber(value / 1_000, 1)} mil`;
  return `R$ ${formatNumber(value, 2)}`;
}
